<template>
    <b-modal :id="$id('affectationFactorModal')" :visible="visible" scrollable centered no-close-on-backdrop body-class="pt-0" >
        <template #modal-header="{ close }">
            <b-row class="w-100 p-0 m-0 h-100 " align-v="center">
                <b-col cols="10" class="text-left">
                    <h6>{{ $t('FactorAssignments', { name : FactorLabel }) }} <!--Affectations du facteur {{selectedFactor.Label}}--></h6>
                </b-col>
                <b-col cols="2" class=" pr-0 p-1 pl-4 text-right">
                    <b-icon role="button" icon="x-lg" font-scale="1.5" @click="close(); modalClosed()" v-b-tooltip.hover :title="$t('Cancel')" /> <!-- Fermer -->
                </b-col>
            </b-row>
        </template>

        <!-- Affichage du nombre d'affectations sélectionées + barre d'outils de l'arborescence -->
        <b-row cols="1" align-v="center" class="px-3 pt-2 sticky-top bg-white">
            <label>
                {{ $tc('AsignmentsSelected', this.checkedNodeIds.length, { count : this.checkedNodeIds.length }) }} <!--{{nbAllocation}} affectations sélectionnée(s) sur {{maxAllocation}} disponibles-->
            </label>
            <hr class="my-1 bg-muted" />
        </b-row>

        <!-- Affichage arborescence => managepins => false av arbo facteur ? -->
        <b-row cols="1" class="p-0">
            <HeritageTree :Variant="Variant" :IsDraggable="false" :FilterTreeStore="filterTreeStore" :ShowToolbar="false" />
        </b-row>

        <template #modal-footer="{ close, ok }">
            <b-button pill :variant="'outline-' + Variant" @click="close(); modalClosed()">{{ $t('Cancel') }} <!--Annuler--></b-button>
            <b-button pill :variant="Variant" @click="ok(); applySelection();">{{ $t('Apply') }} <!--Appliquer--></b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
    import { Vue, InjectReactive, Prop } from 'vue-property-decorator';
    import Component from 'vue-class-component';

    /** Arborescence pour affectations facteur */
    import { SELECTOR_FILTER_TREE } from '@/App.settings';
    import HeritageTree from '@/components/commons/HeritageTree.vue';
    import { FilterTreeBuilder } from '@/components/commons/FilterTreeBuilder';
    import Pins from "@/components/commons/Pins.vue";
    import TreeNode from '@/types/TreeNode';
    import Pin from '@/types/FilterTree/Pin';
    import { FilterTreeUtils } from '@/components/commons/Utils';

    /** Pour FactorsConfigTree afin de filtrer le sélecteur d'arborescence */
    import IConfigTree from '@/interfaces/IConfigTree';
    import SelectModeEnum from '@/enums/SelectModeEnum';

    /** Traductions */
    import locales from './locales/Factors.json';

    /** Permet d'afficher une modale pour les affectations de facteurs */
    @Component({
        components: {
            HeritageTree,
            Pins
        },
        i18n : { messages : locales }
    })
    export default class Factors extends Vue {
		/** Variant de couleur à appliquer sur le composant */
		@Prop({ default: () => "secondary"})
		public Variant!: string;

        /** Mode de sélection de l'arborescence */
        protected selectMode: SelectModeEnum = SelectModeEnum.MultiSimple;

        /** Reçoit d'un parent le nom de l'instance de FilterTree à utiliser comme base de clonage pour tous les composants enfants du Workbench */
        @InjectReactive()
        private sourceFilterTree!: string;

        /** Liste d'affectations */
        @Prop()
        public Allocations!: string[];
        /** Liste de type d'objets */
        @Prop()
        public ObjectTypes!: string[];

        /** @TODO : Alexandre */                
        private checkedNodeIds: string[] = [] as string[];

        /** @TODO : Alexandre */                
        private selectionNode: TreeNode = {} as TreeNode;

        /** Propriété d'affichage de la modale des affectations de facteurs
         * @description n'est pas une prop car on veut afficher la modale apres le chargement de l'arborescence
         */
        private visible: boolean = false;

        /** Nom du facteur */
        @Prop()
        public FactorLabel!: string;

        /** Builder d'arborescence */
		private filterTreeBuilder!: FilterTreeBuilder;

        /** Indique le nom du nouveau store Vuex à instancier pour le sélecteur */
		private filterTreeStore: string = SELECTOR_FILTER_TREE + 'Factor';

        public async StartSelection(selectionNode: TreeNode, checkedNodeIds: string[]) {
            this.selectionNode = selectionNode;
            this.checkedNodeIds = checkedNodeIds;
            /** @todo Initialiser cette instance à lors du clic et la supprimer lors de la fermeture de la fenêtre ==> à déplacer dans un autre composant pour gérer le destroy ? */
            /** Indique le nom de l'instance de FilterTree à utiliser comme base de clonage pour tous les composants enfants du Workbench */
			this.filterTreeBuilder = new FilterTreeBuilder(this.$store, this.filterTreeStore);

            // On génère les Pins attendus pour le mode MultiSimpleMode
            let initialPins: Pin[] = [];
            let maxId = 0;
            for (let id of this.checkedNodeIds) {
                maxId++;
                /** On crée un nœud minimal car SetPinTree requiert un objet.
                Les propriétés manquantes n'ont pas d'importance, car MultiSimpleMode écrasera 'pin.TreeNode' avec la vraie instance d'arbre lors du Clone.  */
                let node = { Id: id, ObjectType: "", Name: "", Depreciated: false } as TreeNode;
                
                // On génère la Pin avec preview à false pour que 'Selections' soit rempli avec l'Id
                let newPin = FilterTreeUtils.SetPinTree(node, false, maxId);
                
                initialPins.push(newPin);
            }

            await this.filterTreeBuilder.Clone(this.sourceFilterTree, initialPins,
                new FactorsConfigTree(
                    this.Allocations,
                    this.ObjectTypes,
                    this.checkedNodeIds
                ), SelectModeEnum.MultiSimple
            );

            console.debug("Factors - created() - initFilterTree - then()...");
            this.visible = true;
        }

        /** Permet d'appliquer la sélection des noeuds pour le facteur */
        private applySelection() {
            this.visible = false;
            this.$emit('ApplyedEvent', { selectionNode: this.selectionNode, checkedNodeIds: this.$store.getters[SELECTOR_FILTER_TREE + 'Factor/CheckedNodeIds'] });
            console.debug("Factors - applySelection() - this.visible : ", this.visible);
        }

        /** Permet la fermeture de la modal via un evenement */
        private modalClosed() {
            this.visible = false;
            this.$emit('ClosedEvent');
        }

        private unwatchSourceFilterTree = undefined;

        async created() {
            /** Calcul des noeuds sélectionnés */
            this.unwatchSourceFilterTree = this.$store.watch((state, getters) => getters[SELECTOR_FILTER_TREE + 'Factor/CheckedNodeIds'], (val: string[], oldValue: string[]) => {
                /** Obligé de tester si les InvolvedNodes sont différents car le watcher se déclenche lors du clone de l'arborescence dans FilterTreeBuilder lors du this.$store.registerModule(this.filterTreeStore, FilterTree);
                 * @see https://github.com/vuejs/vuex/issues/524 */
                console.debug("Factors - created() - unwatchSourceFilterTree");
                
                if (JSON.stringify(val) != JSON.stringify(oldValue)) {
                    this.checkedNodeIds = val;
                }
            }, { immediate: true });
            //#endregion Gestion des noeuds affectés pour le facteur
        }

        beforeDestroy() {
            if (this.filterTreeBuilder)
				this.filterTreeBuilder.UnloadFilterTree();
            if (this.unwatchSourceFilterTree)
                this.unwatchSourceFilterTree();
        }
    }

    /** Implémentation de IConfigTree pour filtrer la liste des noeuds de l'arborescence à sa création spécifiquement pour les facteurs */
    class FactorsConfigTree implements IConfigTree {
        /** Liste des ids de noeuds cochés */
        private checkedNodeIds: string[] = [];
        /** Liste d'affectations */
        private allocations: string[] = [];
        /** Liste de type d'objets */
        private objectTypes: string[] = [];
        /** */
        private autoNodesSelection: boolean = false;

        /** 
         * Initialisation de FactorConfigTree
         * @param involvedNodes
         * @param allocations       Liste d'affectations
         * @param objectTypes       Liste de type d'objets
         **/
        public constructor(allocations: string[], objectTypes: string[], checkedNodeIds: string[]) {
            this.allocations = allocations;
            this.objectTypes = objectTypes;
            this.checkedNodeIds = checkedNodeIds;
        }

        /** 
         * Filtre une liste de noeuds
         * @param treeNodes Noeuds à filtrer
         * @param isHistoricMode Indication mode historique
         * @returns         Noeuds filtrés
         */
        public Filter(treeNodes: TreeNode[], isHistoricMode: boolean): TreeNode[] {
            let selecteds: TreeNode[] = [];

            for (let treeNode of treeNodes) {
                if (treeNode.Selections && treeNode.Selections.length > 0
                    && treeNode.Selections.findIndex(x => x.Affiliation === 0) != -1)
                    selecteds.push(treeNode);
            }

            // Mise par défaut des certains paramètres sur chaque noeud de l'arborescence
            for (let treeNode of treeNodes) {
                treeNode.Checked = false;
                treeNode.CheckLeader = false;
                treeNode.Indeterminate = false;
                treeNode.NestedChecked = false;
                treeNode.Disabled = false;
                treeNode.Expanded = false;
                treeNode.Hidden = !(!treeNode.Depreciated || (treeNode.Depreciated && isHistoricMode));
                treeNode.WithCheckBox = false;
                treeNode.Selections = [];
            }

            // On parcours l'arborescence pour la gestion des affiliation à 0 (Noeuds courant)
            for (let treeNode of treeNodes) {
                // On verifie si le noeud est du type d'objet autorisés pour le facteur
                treeNode.WithCheckBox = this.objectTypes.findIndex(x => x === treeNode.ObjectType) != -1;
                treeNode.Disabled = this.allocations.findIndex(u => u === treeNode.Id) == -1;

                if (this.checkedNodeIds.findIndex(u=> u === treeNode.Id) != -1) {
                    /** Géré par initialPins lors de l'appel à filterTreeBuilder.Clone */
                    // treeNode.Checked = true;
                    selecteds.push(treeNode);
                }
            }

            // On parcours l'arborescence pour la gestion des affiliation à 2 (Ancêtres)
            for (let treeNode of treeNodes) {
                for (let selected of selecteds) {
                    if (selected.LeftBound > treeNode.LeftBound && selected.RightBound < treeNode.RightBound) {
                        treeNode.Expanded = true;
                    }
                }
            }

            return treeNodes;
        }
    }
</script>

<style scoped>
</style>